import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import NotificationBell from '../components/NotificationBell';
import useIsMobile from '../hooks/useIsMobile';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

export default function Layout({ children }) {
  const navigate = useNavigate();
  const loc = useLocation();
  const isMobile = useIsMobile();
  const name = localStorage.getItem('doctorName') || '';
  const [suspended, setSuspended] = useState(null); // null = проверяется, true/false после
  const [suspendReason, setSuspendReason] = useState('');
  const [showIntro, setShowIntro] = useState(false); // N-071: приветственный поп-ап «Познакомиться с системой»
  const [offerAccepted, setOfferAccepted] = useState(true); // N-072: пока не проверили — не блокируем
  const [offer, setOffer] = useState(null); // { title, content }
  const [offerBusy, setOfferBusy] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false); // W-006: раскрывающееся меню на мобиле
  const [emailVerified, setEmailVerified] = useState(true); // N-096: пока не проверили — не мигаем баннером
  const [resendBusy, setResendBusy] = useState(false);
  const [resendSent, setResendSent] = useState(false);

  // при переходе на десктопную ширину или смене страницы — задвигаем drawer
  useEffect(() => { if (!isMobile) setDrawerOpen(false); }, [isMobile]);
  useEffect(() => { setDrawerOpen(false); }, [loc.pathname]);

  // N-125: если это impersonation-вкладка (токен в sessionStorage) — чистим только её,
  // не трогая localStorage, где может жить реальная сессия врача в другой вкладке.
  const logout = () => {
    if (sessionStorage.getItem('token')) sessionStorage.clear();
    else localStorage.clear();
    window.location.href = '/login';
  };
  const authHeaders = { Authorization: 'Bearer ' + (sessionStorage.getItem('token') || localStorage.getItem('token')) };

  useEffect(() => {
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    if (!token) return;
    axios.get(`${API}/auth/me`, { headers: { Authorization: 'Bearer ' + token } })
      .then(r => {
        setSuspended(!!r.data.is_suspended);
        setSuspendReason(r.data.suspend_reason || '');
        setEmailVerified(!!r.data.email_verified_at);
        if (!r.data.intro_seen_at) setShowIntro(true);
        setOfferAccepted(r.data.offer_accepted !== false);
        if (r.data.offer_accepted === false) {
          axios.get(`${API}/auth/me/offer`, { headers: authHeaders })
            .then(ro => setOffer(ro.data.version))
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  const acceptOffer = () => {
    setOfferBusy(true);
    axios.post(`${API}/auth/me/offer-accept`, {}, { headers: authHeaders })
      .then(() => setOfferAccepted(true))
      .catch(() => {})
      .finally(() => setOfferBusy(false));
  };

  const dismissIntro = () => {
    setShowIntro(false);
    axios.post(`${API}/auth/me/intro-seen`, {}, { headers: authHeaders }).catch(() => {});
  };
  // «Помощь» рендерится своим отдельным Layout — при переходе он заново запрашивает
  // /auth/me. Без ожидания POST /intro-seen эта проверка иногда успевала пройти раньше,
  // чем сервер записал отметку, и попап открывался повторно уже на новой странице.
  const goToIntro = async () => {
    setShowIntro(false);
    try { await axios.post(`${API}/auth/me/intro-seen`, {}, { headers: authHeaders }); } catch { /* игнор */ }
    navigate('/help');
  };

  const resendVerification = () => {
    setResendBusy(true);
    axios.post(`${API}/auth/me/resend-verification`, {}, { headers: authHeaders })
      .then(() => setResendSent(true))
      .catch(() => {})
      .finally(() => setResendBusy(false));
  };

  // Заблокированному врачу доступна только страница поддержки (написать в ТП) — остальные
  // страницы кабинета заменяются экраном блокировки.
  if (suspended && loc.pathname !== '/support') {
    return (
      <div style={s.blockedPage}>
        <img src="/refero-logo-vert.svg" alt="Réfero" style={s.blockedLogo} />
        <div style={s.blockedTitle}>Ваш кабинет заблокирован</div>
        {suspendReason && <div style={s.blockedReason}>{suspendReason}</div>}
        <button style={s.blockedBtn} onClick={() => navigate('/support')}>Обратиться в ТП</button>
        <button style={s.blockedLogout} onClick={logout}>Выйти</button>
      </div>
    );
  }

  // N-072: новая версия оферты требует повторного акцепта, прежде чем кабинет снова
  // станет рабочим — «Поддержка» доступна, чтобы можно было задать вопрос до принятия.
  if (!offerAccepted && loc.pathname !== '/support') {
    return (
      <div style={s.blockedPage}>
        <img src="/refero-logo-vert.svg" alt="Réfero" style={s.blockedLogo} />
        <div style={s.blockedTitle}>Обновилась оферта Refero</div>
        <div style={s.blockedReason}>
          Чтобы продолжить работу в кабинете, ознакомьтесь с новой редакцией и примите её.
        </div>
        {offer && (
          <div style={s.offerBox}>
            <div style={s.offerBoxTitle}>{offer.title}</div>
            <div style={s.offerBoxText}>{offer.content}</div>
          </div>
        )}
        <button style={s.blockedBtn} onClick={acceptOffer} disabled={offerBusy || !offer}>
          {offerBusy ? 'Сохранение…' : 'Принять оферту'}
        </button>
        <button style={s.blockedLogout} onClick={() => navigate('/support')}>Задать вопрос в ТП</button>
        <button style={{ ...s.blockedLogout, marginTop: 8 }} onClick={logout}>Выйти</button>
      </div>
    );
  }

  const items = [
    { p: '/dashboard', l: 'Дашборд' },
    { p: '/patients', l: 'Пациенты' },
    { p: '/clinics', l: 'Клиники' },
    { p: '/commissions', l: 'Комиссии' },
    { p: '/profile', l: 'Профиль' },
    { p: '/help', l: 'Помощь' },
  ];
  const isActive = (p) => loc.pathname === p || loc.pathname.startsWith(p + '/');
  const impersonating = sessionStorage.getItem('impersonated') === '1';

  return (
    <div style={{ ...s.page, ...(impersonating ? s.pageImpersonating : {}) }}>
      {impersonating && (
        <div style={s.impersonatedBar}>
          Вы просматриваете этот кабинет как администратор Refero.
        </div>
      )}
      {isMobile && drawerOpen && <div style={s.drawerOverlay} onClick={() => setDrawerOpen(false)} />}
      <div style={{
        ...s.sidebar,
        ...(isMobile ? { ...s.sidebarMobile, ...(drawerOpen ? s.sidebarMobileOpen : {}) } : {}),
      }}>
        <img src="/refero-logo-vert.svg" alt="Réfero" style={s.logoImg} />
        {name && <div style={s.docName}>{name}</div>}
        <nav style={s.nav}>
          {items.map(i => (
            <div key={i.p} onClick={() => { navigate(i.p); setDrawerOpen(false); }}
              style={{ ...s.navItem, ...(isActive(i.p) ? s.navActive : {}) }}>{i.l}</div>
          ))}
        </nav>
        <div onClick={() => { navigate('/support'); setDrawerOpen(false); }} style={{ ...s.support, cursor: 'pointer' }}>Связаться с техподдержкой</div>
        <button style={s.logout} onClick={logout}>Выйти</button>
      </div>
      <div style={{ ...s.content, ...(isMobile ? s.contentMobile : {}) }}>
        <div style={s.topBar}>
          {isMobile && (
            <button style={s.burgerBtn} onClick={() => setDrawerOpen(o => !o)} aria-label="Меню">
              <span style={s.burgerLine} /><span style={s.burgerLine} /><span style={s.burgerLine} />
            </button>
          )}
          <div style={{ flex: 1 }} />
          <NotificationBell endpoint="/auth/me/notifications" tokenKey="token" viewAllPath="/notifications" />
        </div>
        {suspended && (
          <div style={s.suspendedBanner}>
            <b>Ваш кабинет заблокирован.</b> {suspendReason}
          </div>
        )}
        {!emailVerified && (
          <div style={s.emailBanner}>
            <span>
              Подтвердите email — мы отправили ссылку на вашу почту при регистрации. Не пришло письмо?{' '}
            </span>
            {resendSent
              ? <b>Письмо отправлено повторно.</b>
              : <button style={s.emailBannerBtn} onClick={resendVerification} disabled={resendBusy}>
                  {resendBusy ? 'Отправка…' : 'Отправить ещё раз'}
                </button>}
          </div>
        )}
        {children}
      </div>
      {showIntro && (
        <div style={s.introOverlay}>
          <div style={s.introModal}>
            <img src="/refero-logo-vert.svg" alt="Réfero" style={s.introModalLogo} />
            <h2 style={s.introModalTitle}>Добро пожаловать в Refero!</h2>
            <p style={s.introModalText}>
              Refero — сервис для врачей, который превращает ваши рекомендации пациентам частных клиник в дополнительный доход.
              Прежде чем начать, стоит за пару минут разобраться, как это работает — так вы не упустите ничего важного.
            </p>
            <div style={s.introModalActions}>
              <button style={s.introModalGhost} onClick={dismissIntro}>Позже</button>
              <button style={s.introModalBtn} onClick={goToIntro}>Познакомиться с системой</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page: { display: 'flex', minHeight: '100vh', background: '#F6F8FB', fontFamily: 'Inter, system-ui, sans-serif' },
  pageImpersonating: { paddingTop: 40 },
  impersonatedBar: {
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 3000,
    background: '#1B2B4B', color: '#fff', textAlign: 'center',
    padding: '10px 16px', fontSize: 13, fontWeight: 600,
  },
  sidebar: { width: 240, background: '#fff', borderRight: '1px solid #eef2f7', display: 'flex', flexDirection: 'column', padding: '24px 0', flexShrink: 0 },
  // W-006: на мобиле сайдбар — выезжающий off-canvas drawer поверх контента
  sidebarMobile: {
    position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 200,
    transform: 'translateX(-100%)', transition: 'transform .25s ease',
    boxShadow: '4px 0 24px rgba(15,23,42,0.15)',
  },
  sidebarMobileOpen: { transform: 'translateX(0)' },
  drawerOverlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 190 },
  burgerBtn: {
    display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 4,
    width: 40, height: 40, background: '#fff', border: '1px solid #eef2f7', borderRadius: 10,
    cursor: 'pointer', flexShrink: 0,
  },
  burgerLine: { width: 18, height: 2, background: '#1B2B4B', borderRadius: 1, margin: '0 auto' },
  logoImg: { width: 120, height: 'auto', padding: '0 24px' },
  docName: { color: '#2EC4B6', fontSize: 13, fontWeight: 600, padding: '8px 24px 20px', borderBottom: '1px solid #eef2f7' },
  nav: { display: 'flex', flexDirection: 'column', gap: 4, padding: '16px 12px', flex: 1 },
  navItem: { padding: '11px 14px', borderRadius: 10, color: '#475569', fontSize: 14, cursor: 'pointer', fontWeight: 500 },
  navActive: { background: '#e9faf7', color: '#149e90', fontWeight: 600 },
  support: { margin: '0 12px 8px', textAlign: 'center', color: '#94a3b8', fontSize: 12, textDecoration: 'none' },
  logout: { margin: '0 12px', background: 'transparent', border: '1px solid #e2e8f0', color: '#64748b', padding: '10px', borderRadius: 10, cursor: 'pointer', fontSize: 13 },
  blockedPage: { minHeight: '100vh', background: '#F6F8FB', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, system-ui, sans-serif', padding: 24, textAlign: 'center' },
  blockedLogo: { width: 140, height: 'auto', marginBottom: 32 },
  blockedTitle: { color: '#1B2B4B', fontSize: 22, fontWeight: 700, marginBottom: 12 },
  blockedReason: { color: '#64748b', fontSize: 14, maxWidth: 420, marginBottom: 28, lineHeight: 1.5 },
  blockedBtn: { background: '#2EC4B6', color: '#fff', border: 'none', textDecoration: 'none', borderRadius: 10, padding: '12px 28px', fontSize: 14, fontWeight: 600, marginBottom: 12, cursor: 'pointer' },
  blockedLogout: { background: 'transparent', border: '1px solid #d7dee8', color: '#64748b', padding: '10px 24px', borderRadius: 10, cursor: 'pointer', fontSize: 13 },
  offerBox: { background: '#fff', border: '1px solid #eef2f7', borderRadius: 12, padding: 20, maxWidth: 560, maxHeight: 320, overflowY: 'auto', textAlign: 'left', marginBottom: 24, boxShadow: '0 4px 16px rgba(27,43,75,0.06)' },
  offerBoxTitle: { color: '#1B2B4B', fontSize: 15, fontWeight: 700, marginBottom: 10 },
  offerBoxText: { color: '#475569', fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' },
  content: { flex: 1, padding: 32, overflow: 'auto', minWidth: 0 },
  contentMobile: { padding: '16px 14px' },
  topBar: { display: 'flex', alignItems: 'center', marginBottom: 16, gap: 10 },
  suspendedBanner: { background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 10, padding: '12px 16px', fontSize: 13, marginBottom: 20, lineHeight: 1.5 },
  emailBanner: { background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: 10, padding: '12px 16px', fontSize: 13, marginBottom: 20, lineHeight: 1.5, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 },
  emailBannerBtn: { background: 'transparent', border: 'none', color: '#92400e', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', fontSize: 13, padding: 0 },
  introOverlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 20 },
  introModal: { background: '#fff', borderRadius: 20, padding: '36px 40px', maxWidth: 460, textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  introModalLogo: { width: 120, height: 'auto', marginBottom: 20 },
  introModalTitle: { color: '#1B2B4B', fontSize: 21, fontWeight: 800, margin: '0 0 14px' },
  introModalText: { color: '#475569', fontSize: 14, lineHeight: 1.6, margin: '0 0 26px' },
  introModalActions: { display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' },
  introModalGhost: { background: 'transparent', border: '1px solid #d7dee8', color: '#64748b', borderRadius: 10, padding: '11px 20px', fontSize: 14, cursor: 'pointer' },
  introModalBtn: { background: '#2EC4B6', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 22px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
};
